/**
 * Wrapper for repeatable MARC fields using useFieldArray.
 * Each field instance renders as its own MarcFieldPanel (bordered card).
 *
 * CZNA-1449 — the next occurrence is added from a quiet dashed outline under the
 * last one ("+ Další citovaný zdroj"), not from a primary button that outshouts the
 * field; an occurrence is removed from the ⋮ in its header. The first occurrence is
 * part of the form's structure and has neither.
 */
import React, { useRef } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { stateKey } from '../config/fieldDefinitionTypes';
import { createEmptyFieldInstance } from '../formTypes';
import MarcFieldPanel from '../fields/MarcFieldPanel';
import IndicatorSelect from '../fields/IndicatorSelect';
import NameTypeIndicatorControl from '../fields/NameTypeIndicatorControl';
import DynamicFieldPanel from './DynamicFieldPanel';
import FieldInstanceMenu from './FieldInstanceMenu';
import { resolveLabel } from './labelResolver';
import { addFieldInstanceId, fieldInstanceMenuId, indicatorSelectId } from './domIds';
/** The outline of the panel the next occurrence will be: dashed, quiet, as wide as the field. */
var addOutlineStyle = {
    display: 'block',
    width: '100%',
    marginBottom: '16px',
    padding: '10px 16px',
    border: '1px dashed #999',
    borderRadius: '6px',
    background: 'transparent',
    color: '#333',
    fontSize: '13px',
    textAlign: 'left',
    cursor: 'pointer',
};
/**
 * "Další citovaný zdroj": the label of the field inside a sentence. Lower-cased only
 * when it is a word — an abbreviation (`ISNI`) keeps its capitals.
 */
var inSentence = function (label) {
    var lower = function (c) { return c.toLocaleLowerCase('cs'); };
    var first = label.charAt(0);
    var second = label.charAt(1);
    var word = first !== lower(first) && second !== '' && second === lower(second) && second !== second.toLocaleUpperCase('cs');
    return word ? lower(first) + label.slice(1) : label;
};
var DynamicRepeatableField = function (_a) {
    var fieldDef = _a.fieldDef, control = _a.control, watch = _a.watch, getValues = _a.getValues, setValue = _a.setValue, showToast = _a.showToast;
    var intl = useIntl();
    var key = stateKey(fieldDef);
    var _b = useFieldArray({
        control: control,
        name: "fields.".concat(key),
    }), fields = _b.fields, append = _b.append, remove = _b.remove;
    var addRef = useRef(null);
    var handleAdd = function () {
        append(createEmptyFieldInstance(fieldDef));
    };
    var hasIndicators = !!(fieldDef.indicator1Def || fieldDef.indicator2Def);
    var renderIndicators = function (index) {
        if (!hasIndicators)
            return null;
        return (React.createElement(React.Fragment, null,
            fieldDef.indicator1Def && (React.createElement(Controller, { name: "fields.".concat(key, ".").concat(index, "._ind1.value"), control: control, render: function (_a) {
                    var field = _a.field;
                    // TASK-115: per-variant heading-type switch with confirm UX.
                    if (fieldDef.indicator1Def.isHeadingTypeSwitch && getValues && setValue) {
                        return (React.createElement(NameTypeIndicatorControl, { fieldDef: fieldDef, indicatorDef: fieldDef.indicator1Def, value: field.value || ' ', onChange: field.onChange, pathPrefix: "fields.".concat(key, ".").concat(index), getValues: getValues, setValue: setValue }));
                    }
                    return (React.createElement(IndicatorSelect, { indicatorDef: fieldDef.indicator1Def, value: field.value || ' ', onChange: field.onChange, inputId: indicatorSelectId("fields.".concat(key, ".").concat(index), 1) }));
                } })),
            fieldDef.indicator2Def && (React.createElement(Controller, { name: "fields.".concat(key, ".").concat(index, "._ind2.value"), control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(IndicatorSelect, { indicatorDef: fieldDef.indicator2Def, value: field.value || ' ', onChange: field.onChange, inputId: indicatorSelectId("fields.".concat(key, ".").concat(index), 2) }));
                } }))));
    };
    var buildHeaderRight = function (index) {
        // First occurrence is part of the form structure and cannot be removed.
        // Render only indicators (if any); skip the remove button entirely for index 0.
        if (index === 0) {
            return hasIndicators ? React.createElement(React.Fragment, null, renderIndicators(index)) : null;
        }
        return (React.createElement(React.Fragment, null,
            renderIndicators(index),
            React.createElement(FieldInstanceMenu, { id: fieldInstanceMenuId("fields.".concat(key, ".").concat(index)), label: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.fieldInstance.actions' }, { tag: fieldDef.tag, n: index + 1 }), onRemove: function () {
                    var _a;
                    remove(index);
                    // The menu went with its occurrence: hand the focus to the outline, not the page.
                    (_a = addRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                } })));
    };
    var label = resolveLabel(fieldDef, intl);
    return (React.createElement("div", null,
        fields.map(function (field, index) { return (React.createElement(MarcFieldPanel, { key: field.id, tag: fieldDef.tag, label: fields.length > 1 ? "".concat(label, " (").concat(index + 1, ")") : label, headerRight: buildHeaderRight(index) },
            React.createElement(DynamicFieldPanel, { fieldDef: fieldDef, pathPrefix: "fields.".concat(key, ".").concat(index), control: control, watch: watch, getValues: getValues, setValue: setValue, showToast: showToast }))); }),
        React.createElement("button", { type: "button", ref: addRef, id: addFieldInstanceId("fields.".concat(key)), style: addOutlineStyle, onClick: handleAdd }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addFieldInstance' }, { label: inSentence(label) }))));
};
export default DynamicRepeatableField;
